import { useTranslation } from "react-i18next";
import { IconLoader2 } from "@tabler/icons-react";
import { Button } from "../../ui/Button";
import { useTrackOnMount } from "../../../hooks/use-track-on-mount";
import {
  gaEventPushOptInAnswered,
  gaEventPushOptInShown,
} from "../../../tools/analytics/domains/onboarding.analytics";
import { useNotificationOptIn } from "../../../domains/notification/hooks/useNotificationOptIn";

/**
 * The push opt-in as a chat card, offered in JuisciGPT from the moment the user
 * asks their first question.
 *
 * This is the half of the coverage the removed signup step used to give that
 * the Explore modal cannot reach: the user who lives in the chat and may never
 * open Explore at all.
 *
 * **Two buttons, not an option list.** It started on `ChatOptions` for the
 * shared band look, but that component numbers its rows because it elicits a
 * CHOICE among peers — rendering "1 Turn on Notifications / 2 Maybe later" made
 * a binary yes/not-now read as a quiz. A primary and a ghost say the same thing
 * without pretending the two answers are equivalent. The glass shell below is
 * deliberately the same as `ChatOptions`': both sit in the composer band and
 * must look like one family.
 *
 * **Stacked ABOVE `ChatInput`, not in its place.** A `ChatSuggestion` takes the
 * composer band over, and during a turn that band holds the stop button —
 * swapping it out to ask about notifications would remove the user's only way
 * to abort the answer. Sitting above the input also means the ask can appear
 * while the agent is still thinking, which is the dead time it is meant to use,
 * and the band's measured height changes ONCE, before the first token lands,
 * instead of jerking the transcript the moment text starts arriving.
 *
 * The primary button calls `enable` straight from the tap:
 * `Notification.requestPermission` must fire synchronously from a user gesture
 * or Safari rejects it and leaves the permission `"default"` forever.
 *
 * Like `PushOptInModal`, the ask reports itself — both events fire here, so a
 * caller cannot leave the opt-in rate without a denominator.
 */
export function PushSuggestionCard({
  onDone,
}: {
  onDone: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { isRequesting, showOpenSettings, enable, openSettings, maybeLater } =
    useNotificationOptIn((outcome) => {
      gaEventPushOptInAnswered({ surface: "gpt_chat_card", outcome });
      onDone();
    });

  // The card is rendered only while the ask is live, so mounting IS the
  // impression. Keyed on the surface, which is what distinguishes this ask from
  // the modal's and the fixup page's.
  useTrackOnMount(
    "push-opt-in-shown",
    { status: "shown", key: "gpt_chat_card" },
    () => {
      gaEventPushOptInShown({ surface: "gpt_chat_card" });
    }
  );

  // `mb-3` for the same reason `ChatAttachmentChips` carries `mb-2`: the band
  // stack has no gap of its own, so whatever sits above the input owns the
  // separation. A notch wider than the chips because this is a full card
  // against another full card, not a chip row.
  return (
    <div className="mb-3 flex w-full flex-col gap-4 rounded-2xl border border-solid border-ds-slate-100 bg-[rgba(253,253,253,0.9)] p-4 shadow-[0px_14px_18px_rgba(13,20,18,0.16)] backdrop-blur-[3px]">
      <div className="flex w-full flex-col gap-1">
        <p className="font-['DM_Sans',sans-serif] font-semibold text-[17px]/[23px] text-ds-slate-900">
          {t("Stay current with science")}
        </p>
        <p className="font-['DM_Sans',sans-serif] text-[15px]/5 tracking-[-0.15px] text-ds-moss-600">
          {showOpenSettings
            ? t(
                "Notifications are currently disabled. Open settings to turn them back on."
              )
            : t(
                "We'll notify you only when impactful articles in your field are published"
              )}
        </p>
      </div>

      <div className="flex w-full flex-col gap-2">
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={isRequesting}
          onClick={showOpenSettings ? openSettings : enable}
        >
          {isRequesting ? (
            <IconLoader2 size={16} className="animate-spin" />
          ) : showOpenSettings ? (
            t("Open settings")
          ) : (
            t("Turn on Notifications")
          )}
        </Button>
        {/* The ghost IS the dismissal — no close cross on top of it. */}
        <Button
          variant="ghost"
          className="w-full"
          disabled={isRequesting}
          onClick={maybeLater}
        >
          {t("Maybe later")}
        </Button>
      </div>
    </div>
  );
}
